import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/lib/auth-context';
import { leadAPI, employeeAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Briefcase, Search, Phone, Mail, Tag, MapPin, Globe, MessageSquare, PhoneCall,
  Plus, ListTodo, Clock, Send, ShieldAlert, ExternalLink, User as UserIcon, LayoutGrid, List, AlertTriangle
} from 'lucide-react';
import { KanbanBoard } from '@/components/KanbanBoard';

export const LEAD_STATUS = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  attempted: { label: 'Attempted', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  contacted: { label: 'Contacted', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  qualified: { label: 'Qualified', cls: 'bg-violet-100 text-violet-700 border-violet-200' },
  proposal: { label: 'Proposal Sent', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  negotiation: { label: 'Negotiation', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  won: { label: 'Won', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  lost: { label: 'Lost', cls: 'bg-red-100 text-red-700 border-red-200' },
};

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'note', label: 'Note' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
];
const OUTCOMES = ['interested', 'callback_requested', 'no_answer', 'not_interested', 'wrong_number', 'converted'];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

function StatusBadge({ status }) {
  const s = LEAD_STATUS[status] || LEAD_STATUS.new;
  return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
}

const LEAD_COLUMNS = Object.keys(LEAD_STATUS).map(k => ({ key: k, label: LEAD_STATUS[k].label, cls: LEAD_STATUS[k].cls }));
const todayStr = () => new Date().toISOString().slice(0, 10);

function LeadKanbanCard({ lead, onView }) {
  const overdue = lead.next_follow_up_date && lead.next_follow_up_date < todayStr() && !['won', 'lost'].includes(lead.status);
  return (
    <Card className="hover:shadow-sm transition-shadow" onClick={() => onView(lead)}>
      <CardContent className="p-2.5 space-y-1.5">
        <p className="text-sm font-medium leading-tight truncate">{lead.name}</p>
        <p className="text-[11px] text-muted-foreground truncate">{lead.product || lead.visa_type || lead.utm_campaign || lead.source}</p>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="truncate">{lead.assignee_name || 'Unassigned'}</span>
          {overdue
            ? <span className="flex items-center gap-0.5 text-destructive font-medium shrink-0"><AlertTriangle size={10} /> Overdue</span>
            : lead.next_follow_up_date && <span className="text-amber-700 shrink-0">{lead.next_follow_up_date}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function LeadsPage() {
  const { user, canAccessLeads } = useAuth();
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, total: 0 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mine, setMine] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'board'
  const [due, setDue] = useState('all'); // 'all' | 'today' | 'overdue'

  const [detail, setDetail] = useState(null);
  const [noteForm, setNoteForm] = useState({ type: 'call', outcome: '', content: '', next_follow_up_date: '' });
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', expected_completion_date: '' });

  const loadLeads = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      if (mine) params.mine = true;
      if (due !== 'all') params.due = due;
      const res = await leadAPI.list(params);
      setLeads(res.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load leads');
    } finally { setLoading(false); }
  }, [statusFilter, search, mine, due]);

  useEffect(() => {
    if (!canAccessLeads) { setLoading(false); return; }
    loadLeads();
    leadAPI.stats().then(r => setStats(r.data)).catch((e) => console.error(e));
    employeeAPI.list().then(r => setEmployees(r.data)).catch((e) => console.error(e));
  }, [loadLeads, canAccessLeads]);

  const refreshDetail = async (id) => {
    try { const res = await leadAPI.get(id); setDetail(res.data); } catch { toast.error('Failed to load lead'); }
  };

  const openDetail = (lead) => { setNoteForm({ type: 'call', outcome: '', content: '', next_follow_up_date: '' }); refreshDetail(lead.id); };

  const changeStatus = async (status) => {
    try { await leadAPI.update(detail.id, { status }); toast.success('Status updated'); refreshDetail(detail.id); loadLeads(); leadAPI.stats().then(r => setStats(r.data)); }
    catch { toast.error('Update failed'); }
  };

  const changeLeadStatus = async (leadId, status) => {
    try {
      await leadAPI.update(leadId, { status });
      toast.success('Status updated');
      loadLeads();
      leadAPI.stats().then(r => setStats(r.data)).catch((e) => console.error(e));
    } catch { toast.error('Update failed'); }
  };

  const changeAssignee = async (assignee_id) => {
    try { await leadAPI.update(detail.id, { assignee_id: assignee_id === 'unassigned' ? '' : assignee_id }); toast.success('Assignee updated'); refreshDetail(detail.id); loadLeads(); }
    catch { toast.error('Update failed'); }
  };

  const submitNote = async () => {
    if (!noteForm.content.trim()) { toast.error('Add what was discussed'); return; }
    try {
      await leadAPI.addNote(detail.id, {
        type: noteForm.type, outcome: noteForm.outcome || undefined,
        content: noteForm.content, next_follow_up_date: noteForm.next_follow_up_date || undefined,
      });
      toast.success('Activity logged');
      setNoteForm({ type: 'call', outcome: '', content: '', next_follow_up_date: '' });
      refreshDetail(detail.id);
    } catch { toast.error('Failed to log activity'); }
  };

  const submitTask = async () => {
    if (!taskForm.title.trim()) { toast.error('Task title required'); return; }
    try {
      await leadAPI.createTask(detail.id, { title: taskForm.title, expected_completion_date: taskForm.expected_completion_date || undefined });
      toast.success('Task created & linked to lead');
      setTaskOpen(false); setTaskForm({ title: '', expected_completion_date: '' });
      refreshDetail(detail.id);
    } catch { toast.error('Failed to create task'); }
  };

  const pipelineOrder = useMemo(() => Object.keys(LEAD_STATUS), []);

  if (!canAccessLeads) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center space-y-3" data-testid="leads-no-access">
        <ShieldAlert size={40} className="mx-auto text-muted-foreground" />
        <h2 className="text-xl font-heading font-semibold">No access to Lead Management</h2>
        <p className="text-muted-foreground text-sm">Ask a super admin to grant you Lead Management access from the Employees page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight flex items-center gap-2"><Briefcase size={22} className="text-primary" /> Leads</h1>
          <p className="text-muted-foreground">Marketing & sales pipeline — {stats.total} total leads</p>
        </div>
      </div>

      {/* Pipeline summary */}
      <div className="flex flex-wrap gap-2" data-testid="lead-pipeline-summary">
        {pipelineOrder.map(st => (
          <button key={st} onClick={() => setStatusFilter(statusFilter === st ? 'all' : st)}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${statusFilter === st ? 'ring-2 ring-primary/40' : ''} ${LEAD_STATUS[st].cls}`}
            data-testid={`pipeline-chip-${st}`}>
            {LEAD_STATUS[st].label} · {stats.by_status?.[st] || 0}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, email, phone..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="lead-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {pipelineOrder.map(st => <SelectItem key={st} value={st}>{LEAD_STATUS[st].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={mine ? 'default' : 'outline'} onClick={() => setMine(m => !m)} data-testid="lead-mine-toggle">My leads</Button>
        {/* Due filter */}
        <div className="flex rounded-lg border overflow-hidden shrink-0" data-testid="lead-due-filter">
          {[['all', 'All'], ['today', 'Due today'], ['overdue', 'Overdue']].map(([k, lbl]) => (
            <button key={k} onClick={() => setDue(k)}
              className={`px-3 py-2 text-xs font-medium transition-colors ${due === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              data-testid={`due-${k}`}>
              {lbl}
            </button>
          ))}
        </div>
        {/* View toggle */}
        <div className="flex rounded-lg border overflow-hidden shrink-0" data-testid="lead-view-toggle">
          <button onClick={() => setView('list')} className={`px-3 py-2 transition-colors ${view === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`} data-testid="view-list" title="List view"><List size={15} /></button>
          <button onClick={() => setView('board')} className={`px-3 py-2 transition-colors ${view === 'board' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`} data-testid="view-board" title="Board view"><LayoutGrid size={15} /></button>
        </div>
      </div>

      {view === 'board' ? (
        loading ? <p className="text-center text-muted-foreground py-12">Loading...</p> :
          leads.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              <Briefcase size={32} className="mx-auto mb-2 opacity-40" />
              No leads match the current filters.
            </div>
          ) : (
            <div>
              <p className="text-xs text-muted-foreground mb-3">Drag leads between columns to change their pipeline stage.</p>
              <KanbanBoard
                columns={LEAD_COLUMNS}
                items={leads}
                getStatus={(l) => l.status}
                getId={(l) => l.id}
                onMove={changeLeadStatus}
                renderCard={(l) => <LeadKanbanCard lead={l} onView={openDetail} />}
              />
            </div>
          )
      ) : (
      <Card>
        <CardContent className="p-0">
          {loading ? <p className="text-center text-muted-foreground py-12">Loading...</p> :
            leads.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">
                <Briefcase size={32} className="mx-auto mb-2 opacity-40" />
                No leads yet. Marketing leads will appear here automatically.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Interest</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Assignee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map(l => (
                      <TableRow key={l.id} className="cursor-pointer" onClick={() => openDetail(l)} data-testid={`lead-row-${l.id}`}>
                        <TableCell>
                          <div className="font-medium">{l.name}</div>
                          <div className="text-xs text-muted-foreground">{l.city || l.country || '—'}</div>
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="flex items-center gap-1"><Phone size={11} /> {l.phone}</div>
                          <div className="flex items-center gap-1 text-muted-foreground"><Mail size={11} /> {l.email}</div>
                        </TableCell>
                        <TableCell className="text-sm">{l.product || l.visa_type || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{l.utm_campaign || l.source}</TableCell>
                        <TableCell className="text-sm">{l.assignee_name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                        <TableCell><StatusBadge status={l.status} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(l.received_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
        </CardContent>
      </Card>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">{detail.name} <StatusBadge status={detail.status} /></DialogTitle>
                <DialogDescription>{detail.product || detail.visa_type || 'Lead'} · received {fmtDate(detail.received_at)}</DialogDescription>
              </DialogHeader>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Left: info + actions */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Status</Label>
                      <Select value={detail.status} onValueChange={changeStatus}>
                        <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.keys(LEAD_STATUS).map(st => <SelectItem key={st} value={st}>{LEAD_STATUS[st].label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Assigned to</Label>
                      <Select value={detail.assignee_id || 'unassigned'} onValueChange={changeAssignee}>
                        <SelectTrigger data-testid="detail-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="text-sm space-y-2 rounded-lg border p-3 bg-muted/30">
                    <div className="flex items-center gap-2"><Phone size={13} className="text-muted-foreground" /> <a href={`tel:${detail.phone}`} className="text-primary">{detail.phone}</a> {detail.call_preference && <span className="text-xs text-muted-foreground">(prefers {detail.call_preference})</span>}</div>
                    <div className="flex items-center gap-2"><Mail size={13} className="text-muted-foreground" /> {detail.email}</div>
                    {(detail.city || detail.country) && <div className="flex items-center gap-2"><MapPin size={13} className="text-muted-foreground" /> {[detail.city, detail.country].filter(Boolean).join(', ')}</div>}
                    {detail.message && <div className="flex items-start gap-2"><MessageSquare size={13} className="text-muted-foreground mt-0.5" /> <span className="italic">{detail.message}</span></div>}
                    {detail.next_follow_up_date && <div className="flex items-center gap-2 text-amber-700"><Clock size={13} /> Follow up: {detail.next_follow_up_date}</div>}
                  </div>

                  <div className="text-xs space-y-1 rounded-lg border p-3">
                    <p className="font-medium text-muted-foreground mb-1">Attribution</p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                      <span><Tag size={10} className="inline mr-1" />Source: {detail.source}</span>
                      <span>Campaign: {detail.utm_campaign || '—'}</span>
                      <span>Medium: {detail.utm_medium || '—'}</span>
                      <span>UTM Source: {detail.utm_source || '—'}</span>
                      <span>Term: {detail.utm_term || '—'}</span>
                      <span>Content: {detail.utm_content || '—'}</span>
                      <span className="col-span-2 truncate">gclid: {detail.gclid || '—'}</span>
                      <span className="col-span-2">Page: {detail.page_slug || '—'}</span>
                    </div>
                    {detail.source_url && <a href={detail.source_url} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1 mt-1"><ExternalLink size={10} /> Source URL</a>}
                  </div>

                  {/* Tasks */}
                  <div className="rounded-lg border p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium flex items-center gap-1.5"><ListTodo size={14} /> Tasks ({detail.tasks?.length || 0})</p>
                      <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => setTaskOpen(true)} data-testid="lead-create-task-btn"><Plus size={12} /> Task</Button>
                    </div>
                    {(detail.tasks || []).length === 0 ? <p className="text-xs text-muted-foreground">No tasks yet.</p> : (
                      <div className="space-y-1.5">
                        {detail.tasks.map(t => (
                          <div key={t.id} className="flex items-center justify-between text-xs">
                            <span>{t.title}</span>
                            <Badge variant="outline" className="text-[10px]">{t.status}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: activity log */}
                <div className="space-y-3">
                  <div className="rounded-lg border p-3 space-y-2 bg-primary/5">
                    <p className="text-sm font-medium flex items-center gap-1.5"><PhoneCall size={14} className="text-primary" /> Log a call / note</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={noteForm.type} onValueChange={v => setNoteForm({ ...noteForm, type: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid="note-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{ACTIVITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={noteForm.outcome || 'none'} onValueChange={v => setNoteForm({ ...noteForm, outcome: v === 'none' ? '' : v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid="note-outcome-select"><SelectValue placeholder="Outcome" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No outcome</SelectItem>
                          {OUTCOMES.map(o => <SelectItem key={o} value={o}>{o.replace(/_/g, ' ')}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea placeholder="What was discussed with the client?" rows={2} value={noteForm.content} onChange={e => setNoteForm({ ...noteForm, content: e.target.value })} data-testid="note-content-input" />
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Label className="text-[10px] text-muted-foreground">Next follow-up</Label>
                        <Input type="date" className="h-8 text-xs" value={noteForm.next_follow_up_date} onChange={e => setNoteForm({ ...noteForm, next_follow_up_date: e.target.value })} data-testid="note-followup-input" />
                      </div>
                      <Button size="sm" className="gap-1 mt-4" onClick={submitNote} data-testid="note-save-btn"><Send size={13} /> Log</Button>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-2">Activity timeline</p>
                    {(!detail.activities || detail.activities.length === 0) ? (
                      <p className="text-xs text-muted-foreground">No activity yet. Log your first call above.</p>
                    ) : (
                      <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                        {detail.activities.map(a => (
                          <div key={a.id} className="border-l-2 border-primary/30 pl-3 text-sm" data-testid={`activity-${a.id}`}>
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-xs flex items-center gap-1"><UserIcon size={10} /> {a.user_name}</span>
                              <span className="text-[10px] text-muted-foreground">{fmtDate(a.created_at)}</span>
                            </div>
                            <div className="text-foreground/80 mt-0.5 text-sm">
                              <Badge variant="outline" className="text-[10px] mr-1 capitalize">{a.type.replace(/_/g, ' ')}</Badge>
                              {a.content}
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                              {a.outcome && <span className="capitalize">Outcome: {a.outcome.replace(/_/g, ' ')}</span>}
                              {a.next_follow_up_date && <span className="text-amber-700">Follow-up: {a.next_follow_up_date}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create task dialog */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">New Task for {detail?.name}</DialogTitle>
            <DialogDescription>Creates a task linked to this lead (assigned to the lead owner).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="e.g., Call back & send Canada PR checklist" data-testid="lead-task-title" />
            </div>
            <div className="space-y-2">
              <Label>Due date</Label>
              <Input type="date" value={taskForm.expected_completion_date} onChange={e => setTaskForm({ ...taskForm, expected_completion_date: e.target.value })} data-testid="lead-task-due" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskOpen(false)}>Cancel</Button>
            <Button onClick={submitTask} data-testid="lead-task-save">Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
